import { useAuth } from '@/store/auth';

import type { AuthSession } from '@logistech/ui-shared';

type Role = AuthSession['user']['role'];

// Copie de ROLE_PERMISSIONS (packages/auth/src/permissions.ts), pour masquer les actions refusées ; l'API reste seule juge.
// À tenir alignée à la main : le paquet auth est côté serveur.
export type Permission =
  | 'tenants.read' | 'tenants.update'
  | 'users.read' | 'users.invite' | 'users.changeRole' | 'users.delete' | 'users.me'
  | 'vehicles.read' | 'vehicles.write' | 'vehicles.delete'
  | 'drivers.read' | 'drivers.write' | 'drivers.delete'
  | 'missions.read' | 'missions.write' | 'missions.delete'
  | 'alerts.read' | 'alerts.write'
  | 'reports.read' | 'reports.generate'
  | 'billing.read' | 'billing.write'
  | 'invoices.read' | 'invoices.write'
  | 'maintenance.read' | 'maintenance.write'
  | 'fuel.read' | 'fuel.write'
  | 'geofences.read' | 'geofences.write'
  | 'notifications.read'
  | 'integrations.read' | 'integrations.write'
  | 'rotations.delete'
  | 'dashboard.read_activity'
  | 'truck_owners.read_financial';

const ALL: Permission[] = [
  'tenants.read', 'tenants.update',
  'users.read', 'users.invite', 'users.changeRole', 'users.delete', 'users.me',
  'vehicles.read', 'vehicles.write', 'vehicles.delete',
  'drivers.read', 'drivers.write', 'drivers.delete',
  'missions.read', 'missions.write', 'missions.delete',
  'alerts.read', 'alerts.write',
  'reports.read', 'reports.generate',
  'billing.read', 'billing.write',
  'invoices.read', 'invoices.write',
  'maintenance.read', 'maintenance.write',
  'fuel.read', 'fuel.write',
  'geofences.read', 'geofences.write',
  'notifications.read',
  'integrations.read', 'integrations.write',
  'rotations.delete',
  'dashboard.read_activity',
  'truck_owners.read_financial',
];

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  OWNER: ALL,
  ADMIN: ALL,
  MANAGER: [
    'tenants.read',
    'users.read', 'users.me',
    'vehicles.read', 'vehicles.write',
    'drivers.read', 'drivers.write',
    'missions.read', 'missions.write',
    'alerts.read', 'alerts.write',
    'reports.read', 'reports.generate',
    'invoices.read', 'invoices.write',
    'maintenance.read', 'maintenance.write',
    'fuel.read', 'fuel.write',
    'geofences.read', 'geofences.write',
    'rotations.delete',
    'dashboard.read_activity',
  ],
  DISPATCHER: [
    'users.read', 'users.me',
    'vehicles.read',
    'drivers.read',
    'missions.read', 'missions.write',
    'alerts.read',
    'fuel.read',
    'geofences.read',
  ],
  DRIVER: ['users.me', 'missions.read'],
  ACCOUNTANT: [
    'tenants.read', 'users.me',
    'reports.read', 'reports.generate',
    'billing.read', 'billing.write',
    'invoices.read', 'invoices.write',
    'fuel.read',
    'dashboard.read_activity',
    'truck_owners.read_financial',
  ],
  VIEWER: ['users.me', 'vehicles.read', 'drivers.read', 'missions.read', 'alerts.read', 'reports.read'],
  // Accès machine (public-api) : aucune action dans la console
  API_PARTNER: [],
};

export const hasPermission = (role: Role | undefined, permission: Permission): boolean =>
  !!role && (ROLE_PERMISSIONS[role]?.includes(permission) ?? false);

/**
 * Permissions effectives de la session (custom role + overrides, calculées par le gateway) ;
 * session antérieure à 5.4.b (pas de `permissions`) → matrice du rôle built-in.
 */
export const sessionHasPermission = (
  user: Pick<AuthSession['user'], 'role' | 'permissions'> | undefined,
  permission: Permission,
): boolean => {
  if (!user) return false;
  if (user.permissions) return user.permissions.includes(permission);
  return hasPermission(user.role, permission);
};

export const useHasPermission = (permission: Permission) => {
  const user = useAuth((s) => s.session?.user);
  return sessionHasPermission(user, permission);
};
